import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api, formatApiError, imgSrc } from "../lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const MAX = 5;

export const InquiryPhotos = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(0);
  const inputRef = useRef(null);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => ACCEPT.split(",").includes(f.type));
    if (!files.length) return;
    const room = MAX - value.length;
    if (room <= 0) {
      toast.error(`You can attach up to ${MAX} photos.`);
      return;
    }
    const batch = files.slice(0, room);
    setUploading((n) => n + batch.length);
    const results = await Promise.allSettled(
      batch.map((f) => {
        const fd = new FormData();
        fd.append("file", f);
        return api.post("/inquiry-uploads", fd).then(({ data }) => data.url);
      }),
    );
    const urls = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    const failed = results.find((r) => r.status === "rejected");
    if (failed) toast.error(formatApiError(failed.reason));
    if (urls.length) onChange([...value, ...urls]);
    setUploading((n) => n - batch.length);
  };

  return (
    <div data-testid="inquiry-photos">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">Reference photos (optional)</span>
        <span className="text-[11px] text-muted2">{value.length}/{MAX}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        data-testid="inquiry-photos-file"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />
      <div className="flex flex-wrap gap-2.5">
        {value.map((url, i) => (
          <div key={url} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-inkline" data-testid={`inquiry-photo-${i}`}>
            <img src={imgSrc(url)} alt={`Reference ${i + 1}`} className="h-full w-full object-cover" />
            <button
              type="button"
              data-testid={`inquiry-photo-remove-${i}`}
              onClick={() => onChange(value.filter((u) => u !== url))}
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-ink/80 text-parchment opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
              aria-label={`Remove reference photo ${i + 1}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {value.length < MAX && (
          <button
            type="button"
            data-testid="inquiry-photos-add"
            disabled={uploading > 0}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-inkline text-muted2 transition-colors hover:border-spice hover:text-spice disabled:opacity-60"
          >
            {uploading > 0 ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            <span className="text-[10px] font-semibold">{uploading > 0 ? "Uploading" : "Add"}</span>
          </button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-muted2">Mood boards, venue shots, or dishes you love — up to {MAX} images, 8MB each.</p>
    </div>
  );
};
